import { useEffect, useState } from 'react';
import { z } from 'zod';
import { pendingUserListItemSchema, type PendingUserListItem } from '@app/shared';
import { apiGet, apiPost, ApiPath } from '../lib/api';
import { AdminLayout } from '../components/admin-layout';
import { Card, Table, Modal } from '../components/ui';

export function PendingUsersPage() {
  const [items, setItems] = useState<PendingUserListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedUser, setSelectedUser] = useState<PendingUserListItem | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  const [rejectingUser, setRejectingUser] = useState<PendingUserListItem | null>(null);
  const [alasanPenolakan, setAlasanPenolakan] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchPendingUsers = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await apiGet(ApiPath.ADMIN.PENDING_USERS, z.array(pendingUserListItemSchema));
      setItems(data);
    } catch (err) {
      console.error('Gagal mengambil data akun pending:', err);
      setError('Gagal memuat daftar pendaftaran akun');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingUsers();
  }, []);

  const handleApprove = async (user: PendingUserListItem) => {
    if (!window.confirm(`Setujui akun pendaftaran ${user.nama}?`)) return;
    setIsSubmitting(true);
    try {
      await apiPost(ApiPath.ADMIN.APPROVE_PENDING(user.id), {}, z.null());
      await fetchPendingUsers();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Gagal menyetujui akun');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRejectSubmit = async () => {
    if (!rejectingUser) return;
    if (!alasanPenolakan.trim()) {
      alert('Alasan penolakan wajib diisi');
      return;
    }
    setIsSubmitting(true);
    try {
      await apiPost(
        ApiPath.ADMIN.REJECT_PENDING(rejectingUser.id),
        { alasan: alasanPenolakan.trim() },
        z.null(),
      );
      setRejectingUser(null);
      setAlasanPenolakan('');
      await fetchPendingUsers();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Gagal menolak akun');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AdminLayout title="Persetujuan Akun">
      <Card style={{ border: '1px solid #E2E8F0', boxShadow: 'none' }}>
        {error && (
          <div
            style={{
              padding: '0.75rem',
              backgroundColor: '#fee2e2',
              color: '#991b1b',
              borderRadius: '0.375rem',
              marginBottom: '1rem',
            }}
          >
            {error}
          </div>
        )}

        {isLoading ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#64748B' }}>
            Memuat data...
          </div>
        ) : items.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#64748B' }}>
            Tidak ada akun yang menunggu persetujuan Admin.
          </div>
        ) : (
          <Table>
            <thead>
              <tr>
                <th>Nama</th>
                <th>Email</th>
                <th>Sekolah</th>
                <th>Jabatan</th>
                <th>Tanggal Daftar</th>
                <th>Status</th>
                <th style={{ textAlign: 'right' }}>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td style={{ fontWeight: 600 }}>{item.nama}</td>
                  <td>{item.email ?? '-'}</td>
                  <td>{item.sekolah}</td>
                  <td>{item.jabatan}</td>
                  <td>{new Date(item.dibuat_pada).toLocaleDateString('id-ID')}</td>
                  <td>
                    <span
                      style={{
                        padding: '0.25rem 0.5rem',
                        borderRadius: '0.25rem',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        backgroundColor: '#fef3c7',
                        color: '#92400e',
                      }}
                    >
                      Menunggu Persetujuan
                    </span>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => {
                          setSelectedUser(item);
                          setIsDetailOpen(true);
                        }}
                        style={btnDetailStyle}
                      >
                        Detail
                      </button>
                      <button
                        onClick={() => handleApprove(item)}
                        disabled={isSubmitting}
                        style={btnApproveStyle}
                      >
                        Setujui
                      </button>
                      <button
                        onClick={() => setRejectingUser(item)}
                        disabled={isSubmitting}
                        style={btnRejectStyle}
                      >
                        Tolak
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      {/* Modal Detail */}
      {isDetailOpen && selectedUser && (
        <Modal
          open={isDetailOpen}
          title="Detail Pendaftaran Akun"
          onClose={() => {
            setIsDetailOpen(false);
            setSelectedUser(null);
          }}
        >
          <div style={{ display: 'grid', gap: '0.75rem' }}>
            <div>
              <strong>Nama Lengkap:</strong> {selectedUser.nama}
            </div>
            <div>
              <strong>Email:</strong> {selectedUser.email ?? '-'}
            </div>
            <div>
              <strong>Sekolah:</strong> {selectedUser.sekolah}
            </div>
            <div>
              <strong>Jabatan:</strong> {selectedUser.jabatan}
            </div>
            <div>
              <strong>Role Pendaftaran:</strong> {selectedUser.role_code}
            </div>
            <div>
              <strong>Tanggal Mendaftar:</strong>{' '}
              {new Date(selectedUser.dibuat_pada).toLocaleString('id-ID')}
            </div>
            <div>
              <strong>Status Verifikasi:</strong> Menunggu Persetujuan Admin
            </div>
          </div>
          <div
            style={{
              marginTop: '1.5rem',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '0.5rem',
            }}
          >
            <button
              onClick={() => {
                const u = selectedUser;
                setIsDetailOpen(false);
                setSelectedUser(null);
                handleApprove(u);
              }}
              style={btnApproveStyle}
            >
              Setujui
            </button>
            <button
              onClick={() => {
                const u = selectedUser;
                setIsDetailOpen(false);
                setSelectedUser(null);
                setRejectingUser(u);
              }}
              style={btnRejectStyle}
            >
              Tolak
            </button>
          </div>
        </Modal>
      )}

      {/* Modal Alasan Penolakan */}
      {rejectingUser && (
        <Modal
          open={Boolean(rejectingUser)}
          title={`Tolak Pendaftaran Akun: ${rejectingUser.nama}`}
          onClose={() => {
            setRejectingUser(null);
            setAlasanPenolakan('');
          }}
        >
          <div style={{ marginBottom: '1rem' }}>
            <label
              htmlFor="alasan"
              style={{ display: 'block', marginBottom: '0.375rem', fontWeight: 500 }}
            >
              Alasan Penolakan *
            </label>
            <textarea
              id="alasan"
              rows={3}
              required
              value={alasanPenolakan}
              onChange={(e) => setAlasanPenolakan(e.target.value)}
              placeholder="Masukkan alasan penolakan registrasi..."
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                boxSizing: 'border-box',
              }}
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
            <button
              onClick={() => {
                setRejectingUser(null);
                setAlasanPenolakan('');
              }}
              style={{
                padding: '0.5rem 1rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                background: '#fff',
                cursor: 'pointer',
              }}
            >
              Batal
            </button>
            <button
              onClick={handleRejectSubmit}
              disabled={isSubmitting}
              style={{
                padding: '0.5rem 1rem',
                border: 'none',
                borderRadius: '0.375rem',
                backgroundColor: '#dc2626',
                color: '#fff',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Konfirmasi Penolakan
            </button>
          </div>
        </Modal>
      )}
    </AdminLayout>
  );
}

const btnDetailStyle = {
  padding: '0.375rem 0.75rem',
  border: '1px solid #d1d5db',
  borderRadius: '0.375rem',
  backgroundColor: '#fff',
  color: '#374151',
  fontSize: '0.875rem',
  cursor: 'pointer',
};

const btnApproveStyle = {
  padding: '0.375rem 0.75rem',
  border: 'none',
  borderRadius: '0.375rem',
  backgroundColor: '#16a34a',
  color: '#fff',
  fontWeight: 600,
  fontSize: '0.875rem',
  cursor: 'pointer',
};

const btnRejectStyle = {
  padding: '0.375rem 0.75rem',
  border: 'none',
  borderRadius: '0.375rem',
  backgroundColor: '#dc2626',
  color: '#fff',
  fontWeight: 600,
  fontSize: '0.875rem',
  cursor: 'pointer',
};
